import type { ICodeEditorProblem } from "./code-editor-problems";
import "./code-editor-shell.scss";

export interface ICodeEditorProblemListProps {
  problems: ICodeEditorProblem[];
  /**
   * What the database said about the last run, when it refused it. The editor's own analysis
   * cannot see a NOT NULL column or a constraint, so this is the only place that error exists
   * once its toast has gone.
   */
  runError?: string | null;
  /** Given, each row takes the caret to what it complains about. */
  onSelect?: (problem: ICodeEditorProblem) => void;
}

const ErrorIcon = (): JSX.Element => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
  </svg>
);

const WarningIcon = (): JSX.Element => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 4l8.5 15h-17L12 4z" strokeLinejoin="round" />
    <path d="M12 10v4M12 16.5v.5" strokeLinecap="round" />
  </svg>
);

/**
 * What the language server has to say, read like a compiler's output: severity, the place in
 * a monospace gutter that lines up down the list, then the message. A row is a button when the
 * host can move the caret, so a complaint is one click from the line that caused it.
 */
export const CodeEditorProblemList = (props: ICodeEditorProblemListProps): JSX.Element => {
  if (props.problems.length === 0 && !props.runError) {
    return <div className="kuika__code-editor__drawer__empty">No problems in this query.</div>;
  }

  return (
    <div className="kuika__code-editor__problems" data-testid="editorProblemList">
      {props.runError && (
        // First, and not a button: it belongs to the run, not to a line of the query.
        <div
          className="kuika__code-editor__problems__row kuika__code-editor__problems__row--error kuika__code-editor__problems__row--static"
          data-testid="editorRunError"
        >
          <span className="kuika__code-editor__problems__severity">
            <ErrorIcon />
          </span>
          <span className="kuika__code-editor__problems__place">Run failed</span>
          <span className="kuika__code-editor__problems__message">{props.runError}</span>
        </div>
      )}
      {props.problems.map((problem: ICodeEditorProblem) => (
        <button
          type="button"
          key={`${problem.lineNumber}:${problem.column}:${problem.message}`}
          className={`kuika__code-editor__problems__row${problem.isError ? " kuika__code-editor__problems__row--error" : ""}`}
          disabled={!props.onSelect}
          onClick={(): void => props.onSelect?.(problem)}
        >
          <span className="kuika__code-editor__problems__severity">
            {problem.isError ? <ErrorIcon /> : <WarningIcon />}
          </span>
          <span className="kuika__code-editor__problems__place">
            Ln {problem.lineNumber}, Col {problem.column}
          </span>
          <span className="kuika__code-editor__problems__message">{problem.message}</span>
        </button>
      ))}
    </div>
  );
};
